import { useParams, useHistory, Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import useHttp from "../../hooks/use-http";
import { endPoints } from "../../config/end-points";
import ReactPaginate from "react-paginate";
import cuteAlert, { cuteToast } from "../UI/cute";
import { useSelector, useDispatch } from "react-redux";
import { authActions } from "../../store/auth";
import { access_specifier } from "../../config/app.config";
import NotFound from "../404";
import "./users.css";

const Users = () => {
  const user_roles = useSelector((state) => state.auth.roles);
  const { page_no } = useParams();
  const { sendRequest } = useHttp();

  const [users, setUsers] = useState([]);
  const [count, setCount] = useState();
  const [search, setSearch] = useState();
  const [load, setIsload] = useState(1);
  const ref = useRef();
  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    document.title = `${endPoints.title} | Users`;
  }, []);

  useEffect(() => {
    const responseData = (response) => {
      if (response.status === 200) {
        if (response.totalPages > 0 && response.content.length === 0) {
          history.push("/users");
        }
        setUsers(response.content);
        setCount(response.totalPages);
        dispatch(authActions.setLoaderStatus({ status: false }));
      } else {
        dispatch(authActions.setLoaderStatus({ status: false }));
        history.push("/404");
      }
    };

    const page = page_no ? page_no : "";
    const url =
      endPoints.getAllusers + "/" + page + (search ? "?search=" + search : "");
    sendRequest({ url: url, method: "GET" }, responseData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page_no, search, load]);

  const handlePageChange = ({ selected }) => {
    if (selected === 0) {
      history.push("/users");
    } else {
      history.push("/users/" + selected);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSearch(ref.current.value);
  };

  const handleDeleteUser = (e, user_id) => {
    e.preventDefault();
    cuteAlert({
      type: "question",
      title: "Delete User?",
      message: "This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
    }).then((result) => {
      if (result === "confirm") {
        sendRequest(
          { url: endPoints.deleteUser, method: "POST", body: { user_id } },
          () => {
            cuteToast({ type: "success", title: "User deleted", timer: 3000 });
            setIsload(parseInt(load) + 1);
          }
        );
      }
    });
  };

  const handleActivateUser = (e, user_id) => {
    e.preventDefault();
    sendRequest(
      { url: endPoints.activateUser, method: "POST", body: { user_id } },
      () => {
        cuteToast({ type: "success", title: "User activated", message: "Access granted", timer: 3000 });
        setIsload(parseInt(load) + 1);
      }
    );
  };

  const handleToggleActive = (user_id, currentActive) => {
    const url = currentActive === 1 ? endPoints.deleteUser : endPoints.activateUser;
    sendRequest(
      { url, method: "POST", body: { user_id } },
      () => {
        setIsload(parseInt(load) + 1);
      }
    );
  };

  const handleIpRestrict = (e, user_id) => {
    e.preventDefault();
    cuteAlert({
      type: "question",
      title: "Remove IP Restriction?",
      message: "This will unlock the user from IP restriction.",
      confirmText: "Yes, Unlock",
      cancelText: "Cancel",
    }).then((result) => {
      if (result === "confirm") {
        sendRequest(
          { url: endPoints.restrictUser, method: "POST", body: { user_id } },
          () => {
            cuteToast({ type: "success", title: "Restriction removed", timer: 3000 });
            setIsload(parseInt(load) + 1);
          }
        );
      }
    });
  };

  const getAccess = (access) => {
    if (access.includes("9")) return access_specifier["9"] || "Bundle Offer";
    if (access.includes("62")) return access_specifier["62"] || "Agency 20";
    if (access.includes("61")) return access_specifier["61"] || "Agency 10";
    if (access.includes("6")) return access_specifier["6"] || "Agency 5";
    if (access.includes("4")) return access_specifier["4"] || "Masterclass";
    if (access.includes("3")) return access_specifier["3"] || "Agency Theme";
    if (access.includes("2")) return access_specifier["2"] || "Unlimited";
    if (access.includes("1")) return access_specifier["1"] || "Fe";
    return "Unknown";
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const access = useSelector((state) => state.auth.access) || '';
  const isAgency = ['6','61','62','9'].some(c => access.includes(c));

  if (user_roles.role_id === 1 && !isAgency) return <NotFound />;

  return (
    <section className="usr-page">
      <div className="container">
        {/* Header */}
        <div className="usr-header">
          <div className="usr-header-left">
            <h2>Users</h2>
            <p>Manage user accounts and permissions</p>
          </div>
          <Link className="usr-create-btn" to="/create-user">
            <i className="bi bi-plus-lg"></i> Create User
          </Link>
        </div>

        {/* Search */}
        <div className="usr-search-row">
          <form onSubmit={handleSubmit} className="usr-search-form">
            <input
              type="text"
              className="usr-search-input"
              placeholder="Search by email..."
              ref={ref}
            />
            <button type="submit" className="usr-search-btn">
              <i className="bi bi-search"></i> Search
            </button>
          </form>
        </div>

        {/* Table */}
        <div className="usr-table-card">
          <div className="usr-table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{width:'13%'}}>User</th>
                  <th style={{width: user_roles.role_id === 3 ? '20%' : '28%'}}>Email</th>
                  {user_roles.role_id === 3 && <th style={{width:'8%'}}>Type</th>}
                  <th style={{width:'8%'}}>Status</th>
                  <th style={{width:'13%'}}>Plan</th>
                  <th style={{width:'11%'}}>Created</th>
                  <th style={{width:'11%'}}>Last Login</th>
                  <th style={{width:'10%'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <span className="usr-name">
                          {user.first_name} {user.last_name}
                        </span>
                        {user.restrict_user && (
                          <span className="usr-restricted">
                            <i className="bi bi-lock-fill" style={{ fontSize: 10 }}></i> IP
                          </span>
                        )}
                      </td>
                      <td className="usr-email">{user.email}</td>
                      {user_roles.role_id === 3 && (
                        <td>
                          <span className={`usr-type-badge usr-type-badge--${user.parent_user_id && user.parent_user_id !== 1 ? "sub" : "direct"}`}>
                            {user.parent_user_id && user.parent_user_id !== 1 ? "Sub-user" : "Direct"}
                          </span>
                        </td>
                      )}
                      <td>
                        <span className={`usr-status usr-status--${user.active === 1 ? "active" : "inactive"}`}>
                          {user.active === 1 ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <span className="usr-type">{getAccess(user.access)}</span>
                      </td>
                      <td className="usr-date">{formatDate(user.createdAt)}</td>
                      <td className="usr-date">{formatDate(user.updatedAt)}</td>
                      <td>
                        <div className="usr-actions">
                          <button
                            className={`usr-toggle ${user.active === 1 ? 'usr-toggle--on' : ''}`}
                            title={user.active === 1 ? 'Deactivate user' : 'Activate user'}
                            onClick={() => handleToggleActive(user.id, user.active)}
                          >
                            <span className="usr-toggle-knob"></span>
                          </button>

                          <Link
                            to={`/users/edit/${user.id}`}
                            className="usr-action-btn"
                            title="Edit"
                          >
                            <i className="bi bi-pencil"></i>
                          </Link>

                          <button
                            className="usr-action-btn usr-action-btn--danger"
                            title="Delete"
                            onClick={(e) => handleDeleteUser(e, user.id)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>

                          {user.restrict_user && user_roles.role_id === 3 && (
                            <button
                              className="usr-action-btn usr-action-btn--warning"
                              title="Remove IP Restriction"
                              onClick={(e) => handleIpRestrict(e, user.id)}
                            >
                              <i className="bi bi-unlock"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className="usr-empty">
                    <td colSpan={user_roles.role_id === 3 ? 8 : 7}>
                      <div className="usr-empty-content">
                        <i className="bi bi-people"></i>
                        No users found
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {count > 0 && (
          <nav className="usr-pagination" aria-label="Pagination">
            <ReactPaginate
              previousLabel={"\u2039"}
              nextLabel={"\u203A"}
              breakLabel="..."
              breakClassName="usr-pg-break"
              breakLinkClassName="usr-pg-link"
              pageCount={count}
              onPageChange={handlePageChange}
              containerClassName="usr-pg-container"
              pageClassName="usr-pg-item"
              pageLinkClassName="usr-pg-link"
              activeClassName="usr-pg-active"
              previousClassName="usr-pg-item usr-pg-prev"
              nextClassName="usr-pg-item usr-pg-next"
              previousLinkClassName="usr-pg-link"
              nextLinkClassName="usr-pg-link"
              disabledClassName="usr-pg-disabled"
              initialPage={page_no ? parseInt(page_no) : 0}
            />
          </nav>
        )}
      </div>
    </section>
  );
};

export default Users;
